import { Result, err } from '@hereisyourgitanton/core';
import { Observable, of, map } from 'rxjs';
import { IOrganizationManager } from '../../../../features/organization-create/application/interfaces/IOrganizationManager';
import { Injectable, inject } from '@angular/core';
import { OrganizationType } from '../../../../features/organization-create/domain/organization-type';
import {
    OrganizationCreateForm,
    OrganizationUpdateForm,
} from '../../../../features/organization-create/domain/organization-form';
import {
    OrganizationAdminApi,
    OrganizationTypeAdminApi,
    CreateOrganizationRequestDto,
    OrganizationStatusEnum,
    UpdateOrganizationRequestDto,
    OrganizationFunctionalTypeEnum,
    OrganizationResponseDto, OrganizationDocumentAdminApi
} from '@tradgine/data-access/tradgine-data';
import { exhaustiveCheck } from '@tradgine/utils';
import { OrganizationModel } from '../../../../features/organization-create/domain/organization.model';
import {
    OrganizationFunctionalType
} from '../../../../features/organization-create/domain/organization-functional-type';
import { OrganizationStatus } from '../../../../features/organization-create/domain/organization-status';
import { isValidationBackendError } from '@tradgine/data-access/backend-errors';
import { formatValidationError } from '../../../../shared/error-handling/format-validation-error';
import { UserManagerApi, UsersApi } from '@tradgine/data-access/auth';
import { OrganizationUser } from '../domain/organization-user.model';
import { toOrganizationUser } from '../mappers/organization-user.mapper';
import { CreateOrganizationUser } from '../domain/create-organization-user.model';
import { toCreateUserRequestDto } from '../mappers/create-user-request-dto.mapper';
import { UpdateOrganizationUser } from '../domain/update-organization-user.model';
import { toUpdateUserRequestDto } from '../mappers/update-user-request-dto.mapper';
import { Role } from '../domain/role.model';
import { toRole } from '../mappers/role.mapper';

@Injectable({ providedIn: 'root' })
export class OrganizationManager implements IOrganizationManager {
    private readonly organizationAdminApi = inject(OrganizationAdminApi);
    private readonly organizationTypeAdminApi = inject(OrganizationTypeAdminApi);
    private readonly organizationDocumentsAdminApi = inject(OrganizationDocumentAdminApi);
    private readonly usersApi = inject(UsersApi);
    private readonly userManagerApi = inject(UserManagerApi)

    create(form: OrganizationCreateForm): Observable<Result<OrganizationModel, Error>> {
        if (!form || !form.organizationTypeId)
            return of(err<OrganizationModel, Error>(new Error('Organization type is required.')));

        const payload = this.toCreatePayload(form);

        return this.organizationAdminApi.create(payload).pipe(
            map((result) =>
                result
                    .map((org) => this.toOrganization(org))
                    .mapErr((err) => {
                        if (isValidationBackendError(err.error)) {
                            return new Error(formatValidationError(err.error));
                        }

                        return new Error(`Failed to create organization: ${err.message}`);
                    })
            )
        );
    }

    update(form: OrganizationUpdateForm): Observable<Result<OrganizationModel, Error>> {
        if (!form.id) return of(err<OrganizationModel, Error>(new Error('Id is required for update.')));

        const payload = this.toUpdatePayload(form);

        return this.organizationAdminApi.update(payload).pipe(
            map((result) =>
                result
                    .map((org) => this.toOrganization(org))
                    .mapErr((err) => {
                        if (isValidationBackendError(err.error)) {
                            return new Error(formatValidationError(err.error));
                        }

                        return new Error(`Failed to update organization: ${err.message}`);
                    })
            )
        );
    }

    getOrganizationTypes(): Observable<Result<OrganizationType[], Error>> {
        return this.organizationTypeAdminApi.getAll().pipe(
            map((result) =>
                result
                    .mapErr(() => new Error('Failed to fetch organization types'))
            )
        );
    }

    getOrganizationById(id: number): Observable<Result<OrganizationModel, Error>> {
        return this.organizationAdminApi.getById(id).pipe(
            map((result) =>
                result
                    .map((org) => this.toOrganization(org))
                    .mapErr((err) => new Error(`Failed to fetch organization with id ${id}: ${err.message}`))
            )
        );
    }

    uploadLogo(orgId: number, file: File): Observable<Result<string, Error>> {
        if (!orgId || !file) return of(err<string, Error>(new Error('Organization id is required.')));

        return this.organizationDocumentsAdminApi.uploadLogo(file, { organizationId: orgId, name: null, description: null }).pipe(
            map((result) =>
                result
                    .map(resp => resp.logoUrl)
                    .mapErr((err) => new Error(`Failed to upload logo for organization ${orgId}: ${err.message}`))
            )
        );
    }

    removeLogo(orgId: number): Observable<Result<void, Error>> {
        if (!orgId) return of(err<void, Error>(new Error('Organization id is required.')));

        return this.organizationDocumentsAdminApi.deleteLogo(orgId).pipe(
            map((result) =>
                result
                    .map(() => undefined)
                    .mapErr((err) => new Error(`Failed to remove logo for organization ${orgId}: ${err.message}`))
            )
        );
    }

    getUsers(orgId: number): Observable<Result<OrganizationUser[], Error>> {
        return this.usersApi.getUsersByOrganization(orgId).pipe(
            map((result) =>
                result
                    .map((dtos) => dtos.map(toOrganizationUser))
                    .mapErr((err) => new Error(`Failed to fetch users: ${err.message}`))
            )
        );
    }

    createUser(user: CreateOrganizationUser): Observable<Result<OrganizationUser, Error>> {
        const dto = toCreateUserRequestDto(user);

        return this.usersApi.create(dto).pipe(
            map((result) =>
                result
                    .map(({ user }) => toOrganizationUser(user))
                    .mapErr((err) => new Error(`Failed to create user: ${err.message}`))
            )
        );
    }

    updateUser(user: UpdateOrganizationUser): Observable<Result<OrganizationUser, Error>> {
        const dto = toUpdateUserRequestDto(user);

        return this.usersApi.update(dto).pipe(
            map((result) =>
                result
                    .map(({ user }) => toOrganizationUser(user))
                    .mapErr((err) => new Error(`Failed to update user: ${err.message}`))
            )
        );
    }

    deleteUser(id: number): Observable<Result<void, Error>> {
        return this.usersApi.delete(id).pipe(
            map((result) =>
                result
                    .map(() => void 0)
                    .mapErr((err) => new Error(`Failed to delete user: ${err.message}`))
            )
        );
    }

    resetUserPassword(id: number): Observable<Result<void, Error>> {
        return this.usersApi.resetPassword(id).pipe(
            map((result) =>
                result
                    .map(() => void 0)
                    .mapErr((err) => new Error(`Failed to reset user password: ${err.message}`))
            )
        );
    }

    getRoles(): Observable<Result<Role[], Error>> {
        return this.userManagerApi.getRoles().pipe(
            map((result) =>
                result
                    .map((dtos) => dtos.map(toRole))
                    .mapErr((err) => new Error(`Failed to fetch roles: ${err.message}`))
            )
        );
    }

    private toOrganization(org: OrganizationResponseDto): OrganizationModel {
        return {
            id: org.id,
            name: org.name,
            taxIdentificationNumber: org.taxIdentificationNumber,
            legalAddress: org.legalAddress,
            phoneNumber: org.phoneNumber,
            email: org.email,
            status: this.toOrganizationStatusDomain(org.status),
            functionalType: this.toOrganizationFunctionalTypeDomain(org.functionalType),
            logoUrl: org.logoUrl,
            organizationType: org.organizationTypeName,
            createdDateTime: org.createdDateTime,
        };
    }

    private toCreatePayload(form: OrganizationCreateForm): CreateOrganizationRequestDto {
        return {
            name: form.name,
            email: form?.email?.trim()?.length === 0 ? null : form.email,
            phoneNumber: form?.phoneNumber?.trim()?.length === 0 ? null : form.phoneNumber,
            legalAddress: form.legalAddress,
            taxIdentificationNumber: form.taxIdentificationNumber,
            organizationTypeId: form.organizationTypeId,
            status: OrganizationStatusEnum.PendingVerification,
            functionalType: this.fromOrganizationFunctionalTypeDomain(form.functionalType),
        };
    }

    private toUpdatePayload(form: OrganizationUpdateForm): UpdateOrganizationRequestDto {
        return {
            id: form.id,
            name: form.name,
            email: form?.email?.trim()?.length === 0 ? null : form.email,
            phoneNumber: form?.phoneNumber?.trim()?.length === 0 ? null : form.phoneNumber,
            legalAddress: form.legalAddress,
            taxIdentificationNumber: form.taxIdentificationNumber,
            organizationTypeId: form.organizationTypeId,
            status: this.fromOrganizationStatusDomain(form.status),
            functionalType: this.fromOrganizationFunctionalTypeDomain(form.functionalType),
        };
    }

    private fromOrganizationFunctionalTypeDomain(type: OrganizationFunctionalType): OrganizationFunctionalTypeEnum {
        switch (type) {
            case 'Universal':
                return OrganizationFunctionalTypeEnum.Universal;
            case 'Seller':
                return OrganizationFunctionalTypeEnum.Seller;
            case 'Buyer':
                return OrganizationFunctionalTypeEnum.Buyer;
            default:
                exhaustiveCheck(type);
        }
    }

    private toOrganizationFunctionalTypeDomain(type: OrganizationFunctionalTypeEnum): OrganizationFunctionalType {
        switch (type) {
            case OrganizationFunctionalTypeEnum.Universal:
                return 'Universal';
            case OrganizationFunctionalTypeEnum.Seller:
                return 'Seller';
            case OrganizationFunctionalTypeEnum.Buyer:
                return 'Buyer';
            default:
                exhaustiveCheck(type);
        }
    }

    private fromOrganizationStatusDomain(status: OrganizationStatus): OrganizationStatusEnum {
        switch (status) {
            case 'PendingVerification':
                return OrganizationStatusEnum.PendingVerification;
            case 'Active':
                return OrganizationStatusEnum.Active;
            case 'Archived':
                return OrganizationStatusEnum.Archived;
            case 'Blocked':
                return OrganizationStatusEnum.Blocked;
            default:
                exhaustiveCheck(status);
        }
    }

    private toOrganizationStatusDomain(status: OrganizationStatusEnum): OrganizationStatus {
        switch (status) {
            case OrganizationStatusEnum.PendingVerification:
                return 'PendingVerification';
            case OrganizationStatusEnum.Active:
                return 'Active';
            case OrganizationStatusEnum.Archived:
                return 'Archived';
            case OrganizationStatusEnum.Blocked:
                return 'Blocked';
            default:
                exhaustiveCheck(status);
        }
    }
}
